import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, orderBy, getDocs } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const ESTADOS = {
  borrador: { label: 'Borrador', color: '#AEB6C4', bg: 'rgba(174,182,196,0.15)' },
  terminado: { label: 'Terminado', color: '#00B8FF', bg: 'rgba(0,184,255,0.15)' },
  impreso_firmado: { label: 'Impreso y firmado', color: '#00D084', bg: 'rgba(0,208,132,0.15)' },
};

export default function Consentimientos() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [consentimientos, setConsentimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [pacientesDB, setPacientesDB] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [nuevo, setNuevo] = useState({
    pacienteNombre: '', pacienteDni: '', pacienteFechaNac: '',
    medico: 'medico1', procedimiento: '', texto: ''
  });

  const canCreate = ['gestion_general', 'recepcion', 'medico'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'consentimientos'), where('clinicaId', '==', clinicaId), orderBy('creadoEn', 'desc'));
    const unsub = onSnapshot(q, snap => { setConsentimientos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    getDocs(query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido')))
      .then(snap => setPacientesDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [clinicaId]);

  async function crearConsentimiento() {
    if (!nuevo.pacienteNombre || !nuevo.procedimiento || !nuevo.texto) return;
    await addDoc(collection(db, 'consentimientos'), {
      ...nuevo, estado: 'borrador', clinicaId,
      creadoPor: currentUser?.email, creadoEn: new Date(),
    });
    await registrarAccion(currentUser?.email, userRole, 'Consentimientos', 'Crear consentimiento', nuevo.pacienteNombre, clinicaId);
    setModalNuevo(false);
    setNuevo({ pacienteNombre: '', pacienteDni: '', pacienteFechaNac: '', medico: 'medico1', procedimiento: '', texto: '' });
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Consentimientos" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>
        {canCreate && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={() => setModalNuevo(true)} style={btnPrimaryStyle}>+ Nuevo consentimiento</button>
          </div>
        )}
        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : consentimientos.length === 0
            ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No hay consentimientos registrados</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {consentimientos.map(c => {
                  const estado = ESTADOS[c.estado] || { label: c.estado, color: theme.secondary, bg: theme.bgCard };
                  const fecha = c.creadoEn?.toDate ? c.creadoEn.toDate().toLocaleDateString('es-AR') : '—';
                  return (
                    <div key={c.id} onClick={() => navigate(`/consentimientos/${c.id}`)} style={{ background: theme.bgCard, borderRadius: '10px', padding: '14px 18px', border: `0.5px solid ${theme.border}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                      onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                    >
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{c.pacienteNombre}</div>
                        <div style={{ fontSize: '12px', color: theme.secondary }}>{c.procedimiento} · {fecha}</div>
                      </div>
                      <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '99px', fontWeight: '500', background: estado.bg, color: estado.color }}>
                        {estado.label}
                      </span>
                    </div>
                  );
                })}
              </div>
        }
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo consentimiento</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ position: 'relative' }}>
                <label style={labelStyle}>NOMBRE DEL PACIENTE *</label>
                <input value={nuevo.pacienteNombre} onChange={e => { const v = e.target.value; setNuevo({...nuevo, pacienteNombre: v}); if (v.length >= 2) setSugerencias(pacientesDB.filter(p => `${p.apellido} ${p.nombre}`.toLowerCase().includes(v.toLowerCase()) || p.dni?.includes(v)).slice(0,5)); else setSugerencias([]); }} placeholder="Apellido, nombre o DNI..." style={inputStyle} />
                {sugerencias.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: theme.bgModal, border: `0.5px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden', marginTop: '2px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                    {sugerencias.map(p => (
                      <div key={p.id} onClick={() => { setNuevo({...nuevo, pacienteNombre: `${p.apellido}, ${p.nombre}`, pacienteDni: p.dni || '', pacienteFechaNac: p.fechaNacimiento || ''}); setSugerencias([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `0.5px solid ${theme.border}` }} onMouseEnter={e => e.currentTarget.style.background = theme.bgCard} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                        <div style={{ fontSize: '11px', color: theme.secondary }}>DNI {p.dni}</div>
                      </div>
                    ))}
                    <div onClick={() => setSugerencias([])} style={{ padding: '8px 14px', fontSize: '12px', color: theme.secondary, cursor: 'pointer', textAlign: 'center' }}>Continuar con nombre nuevo</div>
                  </div>
                )}
              </div>
              <div><label style={labelStyle}>DNI</label><input value={nuevo.pacienteDni} onChange={e => setNuevo({...nuevo, pacienteDni: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>FECHA DE NACIMIENTO</label><input type="date" value={nuevo.pacienteFechaNac} onChange={e => setNuevo({...nuevo, pacienteFechaNac: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>MÉDICO</label>
  <select value={nuevo.medico} onChange={e => setNuevo({...nuevo, medico: e.target.value})} style={inputStyle}>
    <option value="">Seleccioná un médico</option>
    {Object.entries(clinicaData?.medicos || {}).filter(([, nombre]) => nombre).map(([key, nombre]) => (
      <option key={key} value={nombre}>{nombre}</option>
    ))}
  </select>
</div>
              <div><label style={labelStyle}>PROCEDIMIENTO *</label><input value={nuevo.procedimiento} onChange={e => setNuevo({...nuevo, procedimiento: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>TEXTO DEL CONSENTIMIENTO *</label><textarea value={nuevo.texto} onChange={e => setNuevo({...nuevo, texto: e.target.value})} rows={6} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearConsentimiento} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}